import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, mergeProps as _mergeProps, normalizeStyle as _normalizeStyle, withKeys as _withKeys } from "vue";
const _hoisted_1 = ["src"];
const _hoisted_2 = {
    key: 2,
    class: "ai-chat-text expanded-title"
};
const _hoisted_3 = ["src"];
const _hoisted_4 = ["innerHTML"];
const _hoisted_5 = { key: 0 };
const _hoisted_6 = {
    key: 1,
    class: "prompt-chips"
};
const _hoisted_7 = ["onClick"];
import { ref, computed, watch, onMounted, onUnmounted, nextTick, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useDarkMode } from '@/composables/useDarkMode';
import { useCustomer } from '@/composables/useCustomer';
import { useChat } from '@/composables/useChat';
import { useApi } from '@/composables/useApi';
import { getHttpInstance, getApiConfig } from '@/composables/useApi';
const idleTimeout = 20000;
export default /*@__PURE__*/ _defineComponent({
    __name: 'AiChatBanner',
    props: {
        examples: { default: undefined },
        prompts: { default: undefined },
        dynamicSuggestions: { type: Boolean, default: false },
        context: { default: '' }
    },
    setup(__props, { expose: __expose }) {
        const route = useRoute();
        const router = useRouter();
        const state = useAppState();
        const { isDarkMode } = useDarkMode();
        const { getCustomer } = useCustomer();
        const { sendChatMessage, sendSessionlessChatMessage } = useChat();
        const { signalV1 } = useApi();
        const instance = getCurrentInstance();
        const whatsAppNumber = instance?.appContext.config.globalProperties.$whatsAppNumber || '';
        const props = __props;
        const resolvedExamples = computed(() => {
            return props.prompts ?? props.examples ?? route.meta.aiExamples;
        });
        const suggestedFollowUps = ref([]);
        const suggestRequestId = ref(0);
        const awaitingSuggestions = ref(false);
        function fetchSuggestions(metaPrompt) {
            if (!props.dynamicSuggestions)
                return;
            suggestedFollowUps.value = [];
            awaitingSuggestions.value = true;
            const reqId = ++suggestRequestId.value;
            sendSessionlessChatMessage(metaPrompt, (res) => {
                if (reqId !== suggestRequestId.value)
                    return;
                awaitingSuggestions.value = false;
                try {
                    const body = res?.body ?? '';
                    const match = body.match(/\[[\s\S]*\]/);
                    if (!match)
                        return;
                    const parsed = JSON.parse(match[0]);
                    if (Array.isArray(parsed)) {
                        suggestedFollowUps.value = parsed
                            .filter((s) => typeof s === 'string' && s.trim().length > 0)
                            .slice(0, 3);
                        scrollToEnd();
                    }
                }
                catch (e) {
                    // silently ignore — chips just won't appear this turn
                }
            });
        }
        function requestFollowUpSuggestions(assistantReply) {
            fetchSuggestions(`The assistant just replied to the user with: """${assistantReply}""". ` +
                `Suggest up to 3 short follow-up questions or replies the user might send next. ` +
                `Include Yes/No if the assistant's reply invites that. ` +
                `Don't include please or similar politeness markers. ` +
                `Each must be under 8 words. Respond ONLY with a JSON array of strings, no prose, no markdown.`);
        }
        function requestInitialSuggestions() {
            const topicHint = (props.prompts && props.prompts.length > 0)
                ? `The page they are on covers topics like: ${props.prompts.join('; ')}. `
                : '';
            const contextHint = props.context
                ? `User-specific context (treat as authoritative facts about the user's current situation): ${props.context}. ` +
                    `Strict rules: (1) Items listed as "on file" or with values are things the user ALREADY HAS — never ask when they will receive, get, or arrive. ` +
                    `(2) NEVER suggest questions about documents, forms, accounts, or items not present in this context. ` +
                    `(3) If the context says the user does not have something, do not ask about it. ` +
                    `(4) The topic hints below are just themes for the page — they are NOT statements about what the user has or wants. `
                : '';
            fetchSuggestions(`A user is opening a financial assistant chat. ${contextHint}${topicHint}` +
                `Suggest up to 3 short opening questions they might ask, grounded in the user-specific context. ` +
                `Prefer questions about understanding, interpreting, or acting on what they already have. ` +
                `Do NOT include "Yes", "No", or any yes/no style replies — these are opening questions, not answers. ` +
                `Each must be under 8 words. Respond ONLY with a JSON array of strings, no prose, no markdown.`);
        }
        const showPromptChips = computed(() => {
            const last = messages.value[messages.value.length - 1];
            return !!last && last.direction === 'in';
        });
        const YES_NO_OPENERS = /\b(would you|do you|does (this|that|it)|did (this|that|it)|is (this|that|it)|was (this|that|it)|shall i|shall we|want me to|should i|should we|can i|can we|could i|could we|may i|are you|have you|has (this|that|it)|will you|would it|would that)\b/i;
        const activeChips = computed(() => {
            const last = messages.value[messages.value.length - 1];
            if (!last || last.direction !== 'in')
                return [];
            const body = last.body || '';
            const lastSentence = body.split(/(?<=[.!?])\s+/).pop() || body;
            const isYesNo = props.dynamicSuggestions && lastSentence.trim().endsWith('?') && YES_NO_OPENERS.test(lastSentence);
            if (suggestedFollowUps.value.length > 0) {
                return isYesNo ? ['Yes', 'No', ...suggestedFollowUps.value] : suggestedFollowUps.value;
            }
            if (isYesNo)
                return ['Yes', 'No'];
            if (awaitingSuggestions.value)
                return [];
            const conversationStarted = messages.value.some(m => m.direction === 'out');
            if (conversationStarted)
                return [];
            return props.prompts ?? [];
        });
        const expanded = ref(false);
        const cardShape = ref(false);
        const currentIndex = ref(0);
        let intervalId = null;
        const endRef = ref(null);
        const canvasRef = ref(null);
        const bannerRef = ref(null);
        const inputBarRef = ref(null);
        const message = ref(null);
        const messages = ref([]);
        const typing = ref(false);
        let inputIdleTimer = null;
        const idlePromptShown = ref(false);
        const userHasTyped = ref(false);
        const lastWhatsAppSentIndex = ref(0);
        const cachedVoices = ref([]);
        const initialised = ref(false);
        const prompts = [
            "Feel free to ask me anything, don't be shy 😊",
            "What can I help you with today?",
            "Ask away!",
            "How can I assist you today?",
            "What's on your mind today? 🧠",
            "I'm here to help with your financial questions. 🙋‍♀️",
            "Let's talk about your goals and how I can assist you.",
            "Let's talk finances. 💬",
            "Here to help with your financial journey. 🛤️",
            "I'm ready when you are. ✅",
            "You wait - time passes. ⏳",
            "How can I help you manage your money? 🤝",
            "Ask me anything about your finances. 🤔",
            "Your financial well-being is my priority. 💼",
            "Let's work together to achieve your financial goals."
        ];
        if (window.speechSynthesis) {
            cachedVoices.value = window.speechSynthesis.getVoices();
            window.speechSynthesis.onvoiceschanged = () => {
                cachedVoices.value = window.speechSynthesis.getVoices();
            };
        }
        const hasUnsentMessages = computed(() => {
            return messages.value.slice(lastWhatsAppSentIndex.value).some(m => !m.whatsAppConfirmation);
        });
        function onBannerClick() {
            if (!expanded.value)
                expand();
        }
        let chatTouchStart = null;
        function expand() {
            if (expanded.value)
                return;
            expanded.value = true;
            cardShape.value = true;
            if (!initialised.value) {
                initChat();
                initialised.value = true;
            }
            idlePromptShown.value = false;
            userHasTyped.value = false;
            resetInputIdleTimer();
            scrollToEnd();
            // Stop iOS from scrolling the page when focusing the chat input.
            chatTouchStart = (e) => {
                const target = e.target;
                const wrapper = target?.closest('.chatInput');
                if (!wrapper)
                    return;
                const input = wrapper.querySelector('input');
                if (!input || input === document.activeElement)
                    return;
                e.preventDefault();
                input.focus({ preventScroll: true });
            };
            document.addEventListener('touchstart', chatTouchStart, { passive: false });
        }
        function collapse() {
            expanded.value = false;
            if (inputIdleTimer)
                clearTimeout(inputIdleTimer);
            if (chatTouchStart) {
                document.removeEventListener('touchstart', chatTouchStart);
                chatTouchStart = null;
            }
        }
        function openChat() {
            expand();
        }
        __expose({ openChat });
        function initChat() {
            messages.value.push({
                direction: "in",
                body: "Please remember, the Digital Financial Assistant provides factual information to help you make informed decisions. " +
                    "Any specific actions it may suggest should be taken as guidance, and not advice."
            });
            lastWhatsAppSentIndex.value = messages.value.length;
            getCustomer(() => { });
            requestInitialSuggestions();
        }
        function onKeyPress(event) {
            if (!userHasTyped.value && event.key.length === 1 && event.key.match(/\S/)) {
                userHasTyped.value = true;
            }
            if (event.key === "Enter") {
                if (!message.value || message.value.trim() === "") {
                    event.preventDefault();
                    return;
                }
                showSentMessage();
                sendMessageAndWaitForResponse(message.value);
                message.value = "";
                idlePromptShown.value = false;
            }
        }
        function onExpandedShown() {
            scrollToEnd();
            nextTick(() => {
                const el = inputBarRef.value;
                if (!el)
                    return;
                const rect = el.getBoundingClientRect();
                const buffer = 80;
                const overflow = rect.bottom + buffer - window.innerHeight;
                if (overflow > 0) {
                    window.scrollBy({ top: overflow, behavior: 'smooth' });
                }
            });
        }
        function scrollToEnd() {
            nextTick(() => {
                if (canvasRef.value && endRef.value) {
                    canvasRef.value.scrollTop = canvasRef.value.scrollHeight;
                }
            });
        }
        function showSentMessage() {
            messages.value.push({ direction: "out", body: message.value });
            typing.value = true;
            scrollToEnd();
        }
        function sendMessageAndWaitForResponse(msg, store = true) {
            if (msg.toLowerCase() == "litmustest") {
                router.push('/litmustestpage');
                return;
            }
            if (msg.toLowerCase() == "allownoti") {
                signalV1("enableNotifications", "", "", "", "activity");
                return;
            }
            if (msg.toLowerCase() == "testpage") {
                router.push('/test');
                return;
            }
            if (msg.toLowerCase() == "hiddensettings") {
                let current = localStorage.getItem('showHiddenSettings') === 'true';
                localStorage.setItem('showHiddenSettings', (!current).toString());
                localStorage.setItem('useLocalCache', 'true');
                localStorage.setItem('useCachedEndpoints', 'true');
                router.push('/');
                return;
            }
            if (msg.toLowerCase() == "speak") {
                let current = localStorage.getItem('speak') === 'true';
                localStorage.setItem('speak', (!current).toString());
                return;
            }
            if (msg.toLowerCase() == "spendingtreemap") {
                router.push('/spendingtreemap');
                return;
            }
            if (msg.toLowerCase() == "payslips") {
                localStorage.setItem('showPayslips', 'true');
                router.push('/payslips');
                return;
            }
            if (msg.toLowerCase() == "admin") {
                router.push('/admin');
                return;
            }
            if (msg.toLowerCase() == "chatpage") {
                router.push('/chat');
                return;
            }
            if (msg.toLowerCase() == "spendingv2") {
                router.push('/SpendingAnalysis');
                return;
            }
            const voucherCardMatch = msg.toLowerCase().match(/^vouchercard([1-4])$/);
            if (voucherCardMatch) {
                localStorage.setItem('showNewVoucherCard', 'true');
                window.location.href = '/#/?voucherState=' + voucherCardMatch[1];
                window.location.reload();
                return;
            }
            if (msg.toLowerCase() == "vouchercard") {
                localStorage.setItem('showNewVoucherCard', 'true');
                window.location.href = '/#/';
                window.location.reload();
                return;
            }
            if (msg.toLowerCase() == "vouchercardoff") {
                localStorage.setItem('showNewVoucherCard', 'false');
                window.location.href = '/#/';
                window.location.reload();
                return;
            }
            const quizTestMatch = msg.toLowerCase().match(/^test question (\d+)$/);
            if (quizTestMatch) {
                router.push({ path: '/', query: { quiztest: 'true', q: quizTestMatch[1] } });
                return;
            }
            typing.value = true;
            suggestedFollowUps.value = [];
            sendChatMessage(msg, (res) => {
                if (!res.keepThinking)
                    typing.value = false;
                var messageIn = res.body.replaceAll("**", '');
                messages.value.push({ direction: "in", body: messageIn, store: store });
                if (!res.keepThinking)
                    requestFollowUpSuggestions(messageIn);
                if (localStorage.getItem('speak') === 'true') {
                    const utterance = new SpeechSynthesisUtterance(messageIn);
                    utterance.rate = 0.9;
                    utterance.pitch = 0.8;
                    utterance.voice = cachedVoices.value.find(voice => voice.lang === 'en-GB') || null;
                    window.speechSynthesis.speak(utterance);
                }
                scrollToEnd();
            });
        }
        let addedNavHide = false;
        function onInputFocus() {
            document.body.classList.add('hide-nav-bar');
            addedNavHide = true;
            setTimeout(() => {
                inputBarRef.value?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }, 300);
        }
        function onInputBlur() {
            if (addedNavHide)
                document.body.classList.remove('hide-nav-bar');
            addedNavHide = false;
        }
        function sendPrompt(text) {
            if (typing.value)
                return;
            message.value = text;
            showSentMessage();
            sendMessageAndWaitForResponse(text);
            message.value = "";
            idlePromptShown.value = false;
            userHasTyped.value = true;
            resetInputIdleTimer();
        }
        function sendMessage() {
            if (message.value && !typing.value) {
                showSentMessage();
                sendMessageAndWaitForResponse(message.value);
                message.value = "";
                idlePromptShown.value = false;
                userHasTyped.value = true;
                resetInputIdleTimer();
            }
        }
        function formatMessage(text) {
            if (!text)
                return '';
            let html = text
                .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                .replace(/\*([^*]+)\*/g, '<em>$1</em>');
            html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
            html = html.replace(/(<br>)+\s*(\d+\.) /g, '<br>$2 ');
            html = html.replace(/\n\s*- /g, '<br>&bull; ');
            html = html.replace(/\n/g, '<br>');
            return html;
        }
        function processMonetaryAmounts(text) {
            if (localStorage.getItem('randomiseFigures') !== 'true')
                return text;
            const monetaryPattern = /(?:£|$)(?:\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\d+(?:\.\d{2})?)|(?:\d+(?:\.\d{2})?\s*(?:pounds?|pence|p|GBP|USD))/gi;
            return text.replace(monetaryPattern, (match) => {
                if (match.startsWith('£') || match.startsWith('$'))
                    return match.charAt(0) + '####';
                return '####' + match.replace(/^\d+(?:\.\d{2})?/, '');
            });
        }
        function applyMonetaryMask(text) {
            if (localStorage.getItem('randomiseFigures') === 'true')
                return processMonetaryAmounts(text);
            return text;
        }
        function resetInputIdleTimer() {
            if (inputIdleTimer)
                clearTimeout(inputIdleTimer);
            if (idlePromptShown.value || userHasTyped.value)
                return;
            inputIdleTimer = setTimeout(() => {
                if (!typing.value && (!message.value || message.value.trim() === '') && !idlePromptShown.value && expanded.value && !userHasTyped.value) {
                    typing.value = true;
                    setTimeout(() => {
                        typing.value = false;
                        messages.value.push({ direction: "in", body: prompts[Math.floor(Math.random() * prompts.length)] });
                        idlePromptShown.value = true;
                        scrollToEnd();
                    }, 2000);
                }
            }, idleTimeout);
        }
        function sendWhatsAppNotification(body, phoneNumber) {
            const request = { body, whatsApp: true, whatsAppTemplate: false, phoneNumber };
            getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/Notifications", request)
                .catch((result) => { console.log(result, 'WhatsApp notification error'); });
        }
        function attach() {
            if (state.customer) {
                if (state.customer.person.whatsAppEnabled) {
                    messages.value.push({ direction: "in", body: "Check your WhatsApp!" });
                    scrollToEnd();
                    sendWhatsAppNotification("You indicated you wanted to send a document 📄  Just drop it here and we will upload and process it for you 👍", state.customer.person.mobilePhone);
                    window.location.href = `https://wa.me/${whatsAppNumber}`;
                }
                else {
                    router.push('/enablewhatsapp');
                }
            }
        }
        function sendToWhatsApp() {
            if (state.customer) {
                if (state.customer.person.whatsAppEnabled) {
                    var newMessages = messages.value.slice(lastWhatsAppSentIndex.value).filter(m => !m.whatsAppConfirmation);
                    if (newMessages.length === 0)
                        return;
                    var conversation = newMessages.map((m) => (m.direction === 'out' ? 'You' : 'MG') + ': ' + m.body).join('\n');
                    lastWhatsAppSentIndex.value = messages.value.length;
                    messages.value.push({ direction: "in", body: "Conversation sent to your WhatsApp!", whatsAppConfirmation: true });
                    scrollToEnd();
                    sendWhatsAppNotification('Conversation saved from MGChat:\n\n' + conversation, state.customer.person.mobilePhone);
                    window.location.href = `https://wa.me/${whatsAppNumber}`;
                }
                else {
                    router.push('/#/enablewhatsapp');
                }
            }
        }
        onMounted(() => {
            if (resolvedExamples.value && resolvedExamples.value.length > 1) {
                intervalId = setInterval(() => {
                    currentIndex.value = (currentIndex.value + 1) % resolvedExamples.value.length;
                }, 3000);
            }
            watch(message, () => { resetInputIdleTimer(); });
            watch(() => [props.prompts, props.context], () => {
                if (!expanded.value || !initialised.value)
                    return;
                if (idlePromptShown.value)
                    return;
                const conversationStarted = messages.value.some(m => m.direction === 'out');
                if (conversationStarted)
                    return;
                requestInitialSuggestions();
            }, { deep: true });
        });
        onUnmounted(() => {
            if (intervalId)
                clearInterval(intervalId);
            if (inputIdleTimer)
                clearTimeout(inputIdleTimer);
            if (addedNavHide)
                document.body.classList.remove('hide-nav-bar');
            if (chatTouchStart) {
                document.removeEventListener('touchstart', chatTouchStart);
                chatTouchStart = null;
            }
        });
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_list_item_title = _resolveComponent("v-list-item-title");
            const _component_v_list_item = _resolveComponent("v-list-item");
            const _component_v_list = _resolveComponent("v-list");
            const _component_v_menu = _resolveComponent("v-menu");
            const _component_v_text_field = _resolveComponent("v-text-field");
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "bannerRef",
                ref: bannerRef,
                class: _normalizeClass(['ai-chat-banner', { expanded: cardShape.value }]),
                onClick: onBannerClick
            }, [
                _createElementVNode("div", {
                    class: "ai-chat-header",
                    onClick: _cache[1] || (_cache[1] = _withModifiers(() => { }, ["stop"]))
                }, [
                    _createElementVNode("img", {
                        src: _unref(isDarkMode) ? require('@/assets/AIChat.png') : require('@/assets/AI-Chat-24px.svg'),
                        class: "ai-chat-icon",
                        alt: "AI",
                        onClick: _cache[0] || (_cache[0] = ($event) => (expanded.value ? null : expand()))
                    }, null, 8, _hoisted_1),
                    (!expanded.value && resolvedExamples.value && resolvedExamples.value.length > 0)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            class: "ai-chat-scroll",
                            onClick: expand
                        }, [
                            _createVNode(_Transition, {
                                name: "scroll-text",
                                mode: "out-in"
                            }, {
                                default: _withCtx(() => [
                                    (_openBlock(), _createElementBlock("span", {
                                        class: "ai-chat-text",
                                        key: currentIndex.value
                                    }, _toDisplayString(resolvedExamples.value[currentIndex.value]), 1))
                                ]),
                                _: 1
                            })
                        ]))
                        : (!expanded.value)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 1,
                                class: "ai-chat-text",
                                onClick: expand
                            }, "Ask your assistant..."))
                            : (_openBlock(), _createElementBlock("span", _hoisted_2, "Ask your assistant")),
                    (expanded.value)
                        ? (_openBlock(), _createBlock(_component_v_icon, {
                            key: 3,
                            class: "close-icon",
                            onClick: _withModifiers(collapse, ["stop"])
                        }, {
                            default: _withCtx(() => _cache[6] || (_cache[6] = [
                                _createTextVNode("mdi-close")
                            ])),
                            _: 1
                        }))
                        : _createCommentVNode("", true)
                ]),
                _createVNode(_Transition, {
                    name: "slide-down",
                    onAfterEnter: onExpandedShown,
                    onAfterLeave: _cache[5] || (_cache[5] = ($event) => (cardShape.value = false))
                }, {
                    default: _withCtx(() => [
                        (expanded.value)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: "expanded-content",
                                onClick: _cache[4] || (_cache[4] = _withModifiers(() => { }, ["stop"]))
                            }, [
                                _createElementVNode("div", {
                                    class: "messagesCanvas",
                                    ref_key: "canvasRef",
                                    ref: canvasRef
                                }, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(messages.value, (m) => {
                                        return (_openBlock(), _createElementBlock("div", { key: m }, [
                                            _createElementVNode("div", {
                                                class: _normalizeClass('message ' + m.direction)
                                            }, [
                                                (m.direction === 'in')
                                                    ? (_openBlock(), _createElementBlock("img", {
                                                        key: 0,
                                                        class: "message-avatar",
                                                        src: require('@/assets/mg.png'),
                                                        alt: "MG"
                                                    }, null, 8, _hoisted_3))
                                                    : _createCommentVNode("", true),
                                                _createElementVNode("div", {
                                                    class: "message-content",
                                                    innerHTML: formatMessage(applyMonetaryMask(m.body))
                                                }, null, 8, _hoisted_4)
                                            ], 2)
                                        ]));
                                    }), 128)),
                                    (typing.value)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[7] || (_cache[7] = [
                                            _createElementVNode("div", { class: "message in typingIndicator" }, [
                                                _createElementVNode("span", { class: "typingDots" }, [
                                                    _createElementVNode("span"),
                                                    _createElementVNode("span"),
                                                    _createElementVNode("span")
                                                ])
                                            ], -1)
                                        ])))
                                        : _createCommentVNode("", true),
                                    (activeChips.value.length > 0 && showPromptChips.value && !typing.value)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeChips.value, (p) => {
                                                return (_openBlock(), _createElementBlock("button", {
                                                    key: p,
                                                    type: "button",
                                                    class: "prompt-chip",
                                                    onClick: ($event) => (sendPrompt(p))
                                                }, _toDisplayString(p), 9, _hoisted_7));
                                            }), 128))
                                        ]))
                                        : _createCommentVNode("", true),
                                    _createElementVNode("div", {
                                        style: { "clear": "both" },
                                        ref_key: "endRef",
                                        ref: endRef
                                    }, null, 512)
                                ], 512),
                                _createElementVNode("div", {
                                    class: "inputBarInner",
                                    ref_key: "inputBarRef",
                                    ref: inputBarRef
                                }, [
                                    _createVNode(_component_v_text_field, {
                                        modelValue: message.value,
                                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((message).value = $event)),
                                        class: _normalizeClass(["chatInput", { 'chatInputShrink': message.value && message.value.trim() !== '' }]),
                                        type: "text",
                                        placeholder: "Enter your message",
                                        onKeypress: onKeyPress,
                                        onFocus: onInputFocus,
                                        onBlur: onInputBlur,
                                        "hide-details": "",
                                        onKeyup: _withKeys(sendMessage, ["enter"]),
                                        variant: "solo",
                                        flat: true,
                                        "single-line": "",
                                        outlined: "",
                                        rounded: "",
                                        dense: "",
                                        key: "input"
                                    }, {
                                        "prepend-inner": _withCtx(() => [
                                            _createVNode(_component_v_menu, { location: "top" }, {
                                                activator: _withCtx(({ props: menuProps }) => [
                                                    _createVNode(_component_v_icon, _mergeProps(menuProps, {
                                                        class: "sendIcon",
                                                        style: { "font-size": "24px", "margin-right": "10px", "color": "#8c8897" }
                                                    }), {
                                                        default: _withCtx(() => _cache[8] || (_cache[8] = [
                                                            _createTextVNode("mdi-plus-circle-outline")
                                                        ])),
                                                        _: 2
                                                    }, 1040)
                                                ]),
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_list, {
                                                        density: "compact",
                                                        style: { "min-width": "180px" }
                                                    }, {
                                                        default: _withCtx(() => [
                                                            _createVNode(_component_v_list_item, { onClick: attach }, {
                                                                prepend: _withCtx(() => [
                                                                    _createVNode(_component_v_icon, { style: { "color": "#8c8897" } }, {
                                                                        default: _withCtx(() => _cache[9] || (_cache[9] = [
                                                                            _createTextVNode("mdi-paperclip")
                                                                        ])),
                                                                        _: 1
                                                                    })
                                                                ]),
                                                                default: _withCtx(() => [
                                                                    _createVNode(_component_v_list_item_title, null, {
                                                                        default: _withCtx(() => _cache[10] || (_cache[10] = [
                                                                            _createTextVNode("Attach document")
                                                                        ])),
                                                                        _: 1
                                                                    })
                                                                ]),
                                                                _: 1
                                                            }),
                                                            _createVNode(_component_v_list_item, {
                                                                onClick: sendToWhatsApp,
                                                                disabled: !hasUnsentMessages.value
                                                            }, {
                                                                prepend: _withCtx(() => [
                                                                    _createVNode(_component_v_icon, {
                                                                        style: _normalizeStyle('color: ' + (hasUnsentMessages.value ? '#25D366' : '#e2e0e8'))
                                                                    }, {
                                                                        default: _withCtx(() => _cache[11] || (_cache[11] = [
                                                                            _createTextVNode("mdi-whatsapp")
                                                                        ])),
                                                                        _: 1
                                                                    }, 8, ["style"])
                                                                ]),
                                                                default: _withCtx(() => [
                                                                    _createVNode(_component_v_list_item_title, null, {
                                                                        default: _withCtx(() => _cache[12] || (_cache[12] = [
                                                                            _createTextVNode("Save this chat to WhatsApp")
                                                                        ])),
                                                                        _: 1
                                                                    })
                                                                ]),
                                                                _: 1
                                                            }, 8, ["disabled"])
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        "append-inner": _withCtx(() => [
                                            _createVNode(_component_v_icon, {
                                                class: "sendIcon",
                                                color: message.value && message.value.trim() !== '' ? '#8c8897' : '#e2e0e8',
                                                onClick: _cache[2] || (_cache[2] = ($event) => (message.value && message.value.trim() !== '' ? sendMessage() : null)),
                                                style: { "font-size": "24px" }
                                            }, {
                                                default: _withCtx(() => _cache[13] || (_cache[13] = [
                                                    _createTextVNode("mdi-send-outline")
                                                ])),
                                                _: 1
                                            }, 8, ["color"])
                                        ]),
                                        _: 1
                                    }, 8, ["modelValue", "class"])
                                ], 512)
                            ]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                })
            ], 2));
        };
    }
});
